import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useIntegrations } from '../../contexts/IntegrationsContext';
import { useUser } from '../../contexts/UserContext';
import { useBrand } from '../../contexts/BrandContext';
import { getRoleDisplayName, getRoleBadgeColor } from '../../utils/roleUtils';
import { getPermittedNavigation, hasModuleAccess, MODULES } from '../../utils/simplePermissions';
import { getInitials } from '../../utils/avatarUtils';
import {
  HomeIcon,
  UsersIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  FolderIcon,
  CogIcon,
  XMarkIcon,
  BellIcon,
  CloudArrowUpIcon,
  BoltIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

/**
 * Every route DashboardRouter serves, all of which ARE the Dashboard entry.
 *
 * /my-dashboard and /admin-dashboard are real routes, linked from the switcher inside the
 * dashboard itself, so using that switcher moved the URL off /dashboard and the sidebar
 * stopped highlighting anything at all — the user was still on the dashboard and the
 * navigation said they were nowhere. Part of board #982 ("URL mismatch with sidebar"); it
 * does NOT touch which dashboard /dashboard resolves to, which is a separate product call.
 */
const DASHBOARD_ROUTES = ['/dashboard', '/my-dashboard', '/admin-dashboard', '/'];

/** One definition, used by the single-item and sub-item rows in both the desktop and the
 *  mobile sidebar — the same expression was written out four times. */
// A prefix match lets /jobs/<id> light up Jobs (board #5027), but Jobs and Closed Jobs share
// the /jobs prefix (/jobs vs /jobs/history), so a bare prefix test lit BOTH on Closed Jobs
// (operator, 2026-10-10). The rule is: exact match wins; otherwise the LONGEST nav href that
// prefixes the path wins, so /jobs/history belongs to Closed Jobs alone and /jobs/<uuid>
// still belongs to Jobs.
const isNavActive = (href, pathname, allHrefs = []) => {
  if (pathname === href) return true;
  if (href === '/dashboard' && DASHBOARD_ROUTES.includes(pathname)) return true;
  if (href === '/' || !pathname.startsWith(href + '/')) return false;
  const longer = allHrefs.some((h) => h !== href && h.length > href.length
    && (pathname === h || pathname.startsWith(h + '/')));
  return !longer;
};

const Sidebar = ({ isOpen, onClose, onMenuClick }) => {
  const location = useLocation();
  const { integrations } = useIntegrations();
  const { user, logout } = useUser();
  const { brand } = useBrand();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  /**
   * Board #149. This hard-coded ['Work', 'Sales'] on every mount, so a refresh collapsed
   * whatever you had open and expanded two groups you had not asked for — and, worse, hid the
   * group containing the page you were actually looking at. Jerry was ON Inventory with
   * Operations collapsed underneath him.
   *
   * Restores what the user last had open, and guarantees the ACTIVE route's group is expanded
   * regardless — you should never have to hunt for the page you are already on. The stored
   * value is a preference, so a corrupt or missing one falls back to the old default rather
   * than to nothing.
   */
  const [expandedGroups, setExpandedGroups] = useState(() => {
    let stored = null;
    try {
      const raw = localStorage.getItem('sidebarExpandedGroups');
      stored = raw ? JSON.parse(raw) : null;
    } catch { stored = null; }
    // No preference yet means START CLOSED, not ['Work', 'Sales']. The effect below opens
    // the group containing the current route on every navigation including the first, so
    // that pair was not making anything reachable — it was opening two groups unrelated to
    // wherever the user actually landed, on top of the one they needed. Nothing is lost:
    // the active group still expands, and every explicit choice is still remembered.
    return new Set(Array.isArray(stored) && stored.length ? stored : []);
  });

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      // Persisted so the next load starts where the user left off (board #149).
      try { localStorage.setItem('sidebarExpandedGroups', JSON.stringify([...newSet])); } catch { /* preference only */ }
      return newSet;
    });
  };



  // Board #149: whatever the user last had open, the group containing the page they are ON
  // must be visible. Additive — it opens that one group and leaves every other choice alone,
  // which is the half the old hard-coded default got wrong in both directions (it closed what
  // you opened AND opened what you had not).
  useEffect(() => {
    const activeGroup = getPermittedNavigation(user, integrations).find(
      (item) => item.type === 'group' && (item.items || []).some(
        (sub) => sub.href && location.pathname.startsWith(sub.href)
      )
    );
    if (!activeGroup) return;
    setExpandedGroups(prev => (prev.has(activeGroup.name) ? prev : new Set([...prev, activeGroup.name])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Remove all old navigation building - using new grouped navigation only

  // Get the new grouped navigation and handle logout
  const navigation = getPermittedNavigation(user, integrations).map(item => {
    // Handle logout functionality for items and sub-items
    if (item.type === 'group' && item.items) {
      return {
        ...item,
        items: item.items.map(subItem => {
          if (subItem.isLogout) {
            return {
              ...subItem,
              onClick: () => {
                logout();
                onClose();
              }
            };
          }
          return subItem;
        })
      };
    } else if (item.isLogout) {
      return {
        ...item,
        onClick: () => {
          logout();
          onClose();
        }
      };
    }
    return item;
  });
  // Every href the sidebar knows, so isNavActive can let the longest prefix win.
  const allNavHrefs = navigation.flatMap((item) => [item.href, ...((item.items || []).map((s) => s.href))]).filter(Boolean);

  return (
    <>
      {/* Desktop sidebar - Fixed position */}
      <aside className="app-sidebar hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-64 lg:overflow-y-auto lg:pb-4">
        <div className="app-sidebar__brand flex h-16 shrink-0 items-center px-6">
          <div className="flex items-center">
            <div className="app-brand-mark w-8 h-8 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">{brand.name.charAt(0)}</span>
            </div>
            <span className="app-sidebar__brand-name ml-3 text-xl font-bold">{brand.name}</span>
          </div>
        </div>
        <nav className="mt-8">
          {/* pb-6 so the last nav item's highlight clears the scroll edge at short
              viewport heights (e.g. 1024×768) instead of being clipped flat. */}
          <ul className="space-y-2 px-4 pb-6">
            {navigation.map((item) => {
              if (item.type === 'group') {
                const isExpanded = expandedGroups.has(item.name);
                return (
                  <li key={item.name}>
                    <button
                      onClick={() => toggleGroup(item.name)}
                      className="app-sidebar__item app-sidebar__item--group group flex w-full items-center justify-between rounded-md p-3 text-sm leading-6 font-semibold"
                    >
                      <div className="flex items-center gap-x-3">
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.name}
                      </div>
                      {isExpanded ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>
                    {isExpanded && (
                      <ul className="mt-2 space-y-1 pl-6">
                        {item.items.map((subItem) => {
                          const isActive = isNavActive(subItem.href, location.pathname, allNavHrefs);

                          return (
                            <li key={subItem.name}>
                              {/* Sign Out is an ACTION, and inside a group it was not being
                                  treated as one. The single-item branch below already renders
                                  isLogout as a button; this branch rendered every sub-item as
                                  a Link, so Sign Out became <Link to={undefined}> — an anchor
                                  advertising the current page, which is why it read as
                                  /customers. The onClick the navigation builder attaches was
                                  never passed to it either, so clicking Sign Out inside the
                                  Account group did not sign anyone out; it navigated to the
                                  page they were already on. A button also gives keyboard and
                                  assistive-technology users the right affordance, and stops
                                  open-in-new-tab and copy-link offering a destination that
                                  was never real. */}
                              {subItem.isLogout ? (
                                <button
                                  type="button"
                                  onClick={subItem.onClick}
                                  className="app-sidebar__item app-sidebar__item--sub group flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm leading-6 font-medium transition-colors"
                                >
                                  <subItem.icon className="h-5 w-5 shrink-0" />
                                  {subItem.name}
                                </button>
                              ) : (
                                <Link
                                  to={subItem.href}
                                  className={`app-sidebar__item app-sidebar__item--sub group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-medium transition-colors ${
                                    isActive
                                      ? 'is-active'
                                      : ''
                                  }`}
                                >
                                  <subItem.icon className="h-5 w-5 shrink-0" />
                                  {subItem.name}
                                  {subItem.badge && (
                                    <span className="app-sidebar__badge ml-auto shrink-0 text-xs leading-none text-center whitespace-nowrap px-2 py-1 rounded-full">
                                      {subItem.badge}
                                    </span>
                                  )}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              } else {
                // Single item (Dashboard, Timesheets for employees, etc.)
                const isActive = isNavActive(item.href, location.pathname, allNavHrefs);

                return (
                  <li key={item.name}>
                    {item.isLogout ? (
                      <button
                        onClick={item.onClick}
                        className="app-sidebar__item group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold transition-colors w-full text-left"
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.name}
                      </button>
                    ) : (
                      <Link
                        to={item.href}
                        className={`app-sidebar__item group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold transition-colors ${
                          isActive
                            ? 'is-active'
                            : ''
                        }`}
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.name}
                        {item.badge && (
                          <span className="app-sidebar__badge ml-auto shrink-0 text-xs leading-none text-center whitespace-nowrap px-2 py-1 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    )}
                  </li>
                );
              }
            })}
          </ul>
        </nav>
      </aside>

      {/* Mobile sidebar */}
      {/* #3186. The drawer is moved off-canvas with -translate-x-full, which hides it
          VISUALLY and nothing more: while closed, every link in it — and the Sign out
          button at the bottom — stayed in the tab order and in the accessibility tree,
          sitting at a negative x. That is what a driver reports as "Sign out is
          permanently outside the viewport": it is measuring the closed drawer. A person
          can always reach it (the Topbar hamburger calls setSidebarOpen(true), and the
          panel then slides to translate-x-0), so the control is not lost — but a keyboard
          or screen-reader user tabbing through the page fell into a menu that is not on
          screen. inert takes it out of both; aria-hidden keeps assistive tech in step. */}
      <div
        className={`app-sidebar app-sidebar--mobile lg:hidden fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ touchAction: 'pan-y' }}
        aria-hidden={!isOpen}
        inert={isOpen ? undefined : ''}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header with close button */}
          <div className="app-sidebar__brand flex h-16 shrink-0 items-center justify-between px-6">
            <div className="flex items-center">
              <div className="app-brand-mark w-8 h-8 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">{brand.name.charAt(0)}</span>
              </div>
              <span className="app-sidebar__brand-name ml-3 text-xl font-bold">{brand.name}</span>
            </div>
            <button type="button" aria-label="Close" title="Close"
              onClick={onClose}
              className="app-sidebar__close p-2 rounded-md"
            >
            <XMarkIcon className="h-6 w-6" />
          </button>
          </div>

          {/* Navigation - independently scrollable */}
          <nav className="mt-8 flex-1 overflow-y-auto overscroll-contain -webkit-overflow-scrolling-touch">
            <ul className="space-y-2 px-4 pb-4">
              {navigation.map((item) => {
                if (item.type === 'group') {
                  const isExpanded = expandedGroups.has(item.name);
                  return (
                    <li key={item.name}>
                      <button
                        onClick={() => toggleGroup(item.name)}
                        className="app-sidebar__item app-sidebar__item--group group flex w-full items-center justify-between rounded-md p-3 text-sm leading-6 font-semibold"
                      >
                        <div className="flex items-center gap-x-3">
                          <item.icon className="h-6 w-6 shrink-0" />
                          {item.name}
                        </div>
                        {isExpanded ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </button>
                      {isExpanded && (
                        <ul className="mt-2 space-y-1 pl-6">
                          {item.items.map((subItem) => {
                            const isActive = isNavActive(subItem.href, location.pathname, allNavHrefs);

                            return (
                              <li key={subItem.name}>
                                {/* Same fix as the desktop list above. Here the Link carried
                                    onClick={onClose}, so tapping Sign Out on a phone closed
                                    the drawer and left you signed in — which looks even more
                                    like it worked. */}
                                {subItem.isLogout ? (
                                  <button
                                    type="button"
                                    onClick={subItem.onClick}
                                    className="app-sidebar__item app-sidebar__item--sub group flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm leading-6 font-medium transition-colors"
                                  >
                                    <subItem.icon className="h-5 w-5 shrink-0" />
                                    {subItem.name}
                                  </button>
                                ) : (
                                  <Link
                                    to={subItem.href}
                                    onClick={onClose}
                                    className={`app-sidebar__item app-sidebar__item--sub group flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-medium transition-colors ${
                                      isActive
                                        ? 'is-active'
                                        : ''
                                    }`}
                                  >
                                    <subItem.icon className="h-5 w-5 shrink-0" />
                                    {subItem.name}
                                    {subItem.badge && (
                                      <span className="app-sidebar__badge ml-auto shrink-0 text-xs leading-none text-center whitespace-nowrap px-2 py-1 rounded-full">
                                        {subItem.badge}
                                      </span>
                                    )}
                                  </Link>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  // Single item
                  const isActive = isNavActive(item.href, location.pathname, allNavHrefs);

                  return (
                    <li key={item.name}>
                      {item.isLogout ? (
                        <button
                          onClick={item.onClick}
                          className="app-sidebar__item group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold transition-colors w-full text-left"
                        >
                          <item.icon className="h-6 w-6 shrink-0" />
                          {item.name}
                        </button>
                      ) : (
                        <Link
                          to={item.href}
                          onClick={onClose}
                          className={`app-sidebar__item group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold transition-colors ${
                            isActive
                              ? 'is-active'
                              : ''
                          }`}
                        >
                          <item.icon className="h-6 w-6 shrink-0" />
                          {item.name}
                          {item.badge && (
                            <span className="app-sidebar__badge ml-auto shrink-0 text-xs leading-none text-center whitespace-nowrap px-2 py-1 rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      )}
                    </li>
                  );
                }
              })}
            </ul>
          </nav>

          {/* User section at bottom */}
          <div className="app-sidebar__user mt-auto p-4 border-t">
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="app-sidebar__user-button flex items-center w-full p-2 text-sm rounded-md"
              >
                <div className="app-brand-avatar h-8 w-8 mr-3 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium">
                    {getInitials(user?.full_name)}
                  </span>
                </div>
                <div className="text-left flex-1">
                  <div className="font-medium">{user?.full_name}</div>
                  <div className="app-sidebar__muted text-xs">{user?.email}</div>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>
                      {getRoleDisplayName(user?.role)}
                    </span>
                  </div>
                </div>
              </button>

              {userMenuOpen && (
                <div className="app-sidebar__user-menu absolute bottom-full left-0 mb-2 w-full rounded-md shadow-lg py-1">
                  <Link
                    to="/profile"
                    onClick={() => {
                      setUserMenuOpen(false);
                      onClose();
                    }}
                    className="app-sidebar__menu-item flex items-center w-full px-3 py-2 text-sm"
                  >
                    <UserIcon className="h-4 w-4 mr-2" />
                    My Profile
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setUserMenuOpen(false);
                      onClose();
                    }}
                    className="app-sidebar__menu-item flex items-center w-full px-3 py-2 text-sm"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
